import { memo, useCallback, useEffect, useState } from 'react';

import styled from '@mint-lib/styled';
import { getRouteApi } from '@mint/routing';
import { clone } from 'remeda';

import { Box, Divider, Drawer } from '@myn/mui';

import {
  useGetBuilderSchemaData,
  usePreparedBuilderSchema,
} from '../../hooks/useGetBuilderSchemaData.js';
import { useBuilderTableViewDataSelector } from '../../hooks/useGetBuilderTableViewData.js';
import { useTrackingInitiator } from '../../hooks/usePerformanceTracking.js';
import { compareDimensionColumns } from '../../pages/Builder/BuilderHeader/utils.js';
import { CustomDimension, Dimension } from '../../types/campaignBuilder.js';
import { TRACKING_EVENTS } from '../../types/performanceTracking.js';
import AvailableColumnGroups from './AvailableColumnGroups/index.js';
import DragLayer from './DragLayer/index.js';
import { DEPENDENT_COLUMNS } from './EditModal.constants.js';
import {
  floatGroupItems,
  prepareCampaignColumns,
  updateCurrentDimensions,
} from './EditModal.utils.js';
import EditModalLoader from './EditModalLoader.jsx';
import EditViewColumn from './EditViewColumn/index.js';
import Title from './Title.jsx';

type Props = {
  isOpen?: boolean;
  onClose?: () => void;
  onConfirm?: (
    dimensions: Dimension[],
    onSuccess: () => Promise<void>,
    onFailed: () => void,
  ) => void;
};

const routeApi = getRouteApi(
  '/_secured/campaign/$campaignId/planning/buying-plan',
);

const EditModal = ({
  isOpen = false,
  onClose = () => null,
  onConfirm = () => null,
}: Props) => {
  const { campaignId: campaignCollectionId = '' } = routeApi.useParams();

  const { isLoading: isSchemaLoading } = useGetBuilderSchemaData({
    skip: !isOpen,
  });

  const { builderSchema } = usePreparedBuilderSchema();
  const { data: mediaPlanColumns = [] } =
    useBuilderTableViewDataSelector('columns');

  const [isConfirming, setIsConfirming] = useState<boolean>(false);
  const [isCustomSaveActive, setIsCustomSaveActive] = useState<boolean>(false);

  const [availableDimensions, setAvailableDimensions] = useState<
    CustomDimension[]
  >([]);

  const [currentDimensions, setCurrentDimensions] = useState<Dimension[]>(
    prepareCampaignColumns(mediaPlanColumns),
  );

  const startMeasurePerformance = useTrackingInitiator();

  const handleStartPerformanceMeasure = () => {
    if (currentDimensions.length < mediaPlanColumns.length) {
      startMeasurePerformance(TRACKING_EVENTS.COLUMN_SUBTRACTION);
      return;
    }

    if (currentDimensions.length > mediaPlanColumns.length) {
      startMeasurePerformance(TRACKING_EVENTS.COLUMN_ADDITION);
      return;
    }

    startMeasurePerformance(TRACKING_EVENTS.COLUMN_REORDER);
  };

  const isDimensionColumnsEqual = compareDimensionColumns(
    mediaPlanColumns,
    currentDimensions,
  );

  const handleEditViewSave = useCallback(() => {
    const onSuccess = async () => {
      if (campaignCollectionId && !isDimensionColumnsEqual) {
        handleStartPerformanceMeasure();
      }
      setIsConfirming(false);
    };

    const onFailed = () => {
      setIsConfirming(false);
    };

    setIsConfirming(true);
    onConfirm(currentDimensions, onSuccess, onFailed);
  }, [currentDimensions, onConfirm, isCustomSaveActive]);

  const handleDimensionAddition = useCallback(
    (dimension: Dimension, hoverIndex: number) => {
      const clonedDimensions = clone(currentDimensions);
      const newDimensions = DEPENDENT_COLUMNS[dimension.value]
        ? [
            { ...dimension },
            ...availableDimensions
              .filter((availableDimension) =>
                DEPENDENT_COLUMNS[dimension.value].includes(
                  availableDimension.value,
                ),
              )
              .map((item) => ({ ...item })),
          ]
        : [{ ...dimension }];
      clonedDimensions.splice(hoverIndex, 0, ...newDimensions);
      setCurrentDimensions(clonedDimensions);
    },
    [currentDimensions],
  );

  useEffect(() => {
    const newAvailableDimensions = floatGroupItems(builderSchema);
    setAvailableDimensions(newAvailableDimensions);
  }, [builderSchema]);

  useEffect(() => {
    if (!mediaPlanColumns.length) {
      return;
    }

    const newDimensions = prepareCampaignColumns(mediaPlanColumns);
    setCurrentDimensions(newDimensions);
  }, [mediaPlanColumns]);

  useEffect(() => {
    const newDimensions = updateCurrentDimensions(
      currentDimensions,
      availableDimensions,
    );

    setCurrentDimensions(newDimensions);
  }, [availableDimensions]);

  const handleClose = () => {
    onClose();
    setCurrentDimensions(prepareCampaignColumns(mediaPlanColumns));
  };

  return (
    <>
      <StyledDrawer open={isOpen} anchor="right" onClose={handleClose}>
        <ContentWrapper>
          <Title handleClose={handleClose} handleConfirm={handleEditViewSave} />
          {isSchemaLoading || isConfirming ? (
            <EditModalLoader />
          ) : (
            <Box sx={{ display: 'flex' }}>
              <EditViewColumn
                currentDimensions={currentDimensions}
                setCurrentDimensions={setCurrentDimensions}
                handleDimensionAddition={handleDimensionAddition}
              />
              <Divider orientation="vertical" sx={{ m: '0 16px' }} />
              <AvailableColumnGroups
                groupedDimensions={builderSchema}
                currentDimensions={currentDimensions}
                setCurrentDimensions={setCurrentDimensions}
                onCustomSaveChange={setIsCustomSaveActive}
                handleDimensionAddition={handleDimensionAddition}
              />
            </Box>
          )}
        </ContentWrapper>
      </StyledDrawer>
      <DragLayer />
    </>
  );
};

const ContentWrapper = styled('div')`
  display: flex;
  flex-wrap: wrap;
  z-index: 3;
  width: 672px;

  & > div {
    width: 100%;
  }
`;

const StyledDrawer = styled(Drawer)`
  .MuiDrawer-paper {
    overflow-y: scroll;
  }
`;

export default memo(EditModal);
